/**
 * One source of truth for "has a human touched this slide since AI wrote it?".
 *
 * Both AI features in this panel need the answer and must agree on it: the consultant stays quiet on
 * untouched AI output, and the ideas list must not offer to refresh ideas for a slide nobody has
 * edited. Neither can derive it from `slide.hasBeenEdited` — canvas.saveCanvasModel sets that on
 * every write, including AI's own — so the signal is a content hash stamped at the moment AI last
 * authored the slide.
 *
 * Lives here rather than on SlideAiConsultantController because that controller imports
 * SlideAiController, so the dependency can only run one way. Kept free of controller and logging
 * dependencies so both controllers' test suites can import it without further mocking.
 */

import { buildSlideInventory, getInventoryHash } from "./slideInventory";

export const AI_AUTHORED_HASH_KEY = "aiAuthoredInventoryHash";

/**
 * `metadata.generatedModel` is written two different ways and only one of them means AI wrote the
 * words. Deck generation stores the whole generated slide model there (finalizeSlide), but
 * applySlideImage creates `generatedModel.slideImage` on *any* slide the user generates an image for
 * — including one they typed every word of. Treating that as authorship silences the consultant on
 * hand-written slides, so only a model with content beyond the image counts.
 */
function hasAiAuthoredModel(metadata) {
    return Object.keys(metadata.generatedModel ?? {}).some(key => key !== "slideImage");
}

/**
 * Was this slide's *content* written by AI? Note that `metadata.slidePrompt` is deliberately not a
 * marker — it only records that ideas were once generated for the slide, not that the text on it came
 * from a model. A slide the user typed themselves has none of these, and everything on it is theirs.
 */
export function isAiAuthoredSlide(slide) {
    const metadata = slide?.getMetadata?.() ?? {};
    return !!(
        metadata[AI_AUTHORED_HASH_KEY] ||
        metadata.aiGenerated ||
        metadata.wasAiGenerated ||
        hasAiAuthoredModel(metadata)
    );
}

export function getAiAuthoredBaseline(slide) {
    return slide?.getMetadata?.()?.[AI_AUTHORED_HASH_KEY] ?? null;
}

/**
 * Never throws: callers use this on an idle tick where a malformed canvas must not take out the
 * feature. Returns null when the hash can't be computed, which reads as "unknown" downstream.
 */
export function getSlideContentHash(canvas) {
    if (!canvas) {
        return null;
    }

    try {
        return getInventoryHash(buildSlideInventory(canvas));
    } catch (err) {
        return null;
    }
}

/**
 * Unknown counts as "no edits": if the slide is AI-authored but carries no baseline, or the hash
 * couldn't be computed, we have no evidence the user changed anything. Guessing the other way is what
 * produces the failure this module exists to prevent — telling someone they edited a slide they
 * haven't touched.
 */
export function hasEditsSinceAiAuthored(slide, contentHash) {
    if (!isAiAuthoredSlide(slide)) {
        return true;
    }

    const baseline = getAiAuthoredBaseline(slide);
    if (!baseline || !contentHash) {
        return false;
    }

    return baseline !== contentHash;
}
